import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalytics } from "./useAnalytics";
import { PreScreeningQuestion } from "../types/landingPage";
import { validateBMI as validateBMIUtil } from "../utils/bmiCalculator";
import {
  parseCrossReferralData,
  shouldAutoAdvanceQuestion,
  getAutoAdvanceQuestionIndex,
  buildCrossReferralUrl,
  CrossReferralRedirectConfig,
  shouldRedirectToCrossReferral,
} from "../utils/crossReferral";

/**
 * Cross-referral state and actions returned by the hook
 */
export interface CrossReferralState {
  isFromCrossReferral: boolean;
  fromLandingPageId: string | null;
  prefillAnswers: Record<string, string | string[]>;
  handleCrossReferralRedirect: (config: CrossReferralRedirectConfig) => void;
  shouldRedirect: (selectedOption: any, isDisqualifying: boolean) => boolean;
}

/**
 * Configuration for the useCrossReferral hook
 */
export interface UseCrossReferralConfig {
  landingPageId: string;
  questions: PreScreeningQuestion[];
  currentAnswers: Record<string, string | string[]>;
  updateAnswers: (answers: Record<string, string | string[]>) => void;
  updateQuestionnareState: (state: any) => void;
}

/**
 * Custom hook for handling cross-referral functionality between landing pages
 */
export function useCrossReferral({
  landingPageId,
  questions,
  currentAnswers,
  updateAnswers,
  updateQuestionnareState,
}: UseCrossReferralConfig): CrossReferralState {
  const [searchParams] = useSearchParams();
  const { track } = useAnalytics();

  // Parse cross-referral data from URL
  const crossReferralData = parseCrossReferralData(searchParams);

  // Handle cross-referral redirect
  const handleCrossReferralRedirect = useCallback((config: CrossReferralRedirectConfig) => {
    // Track the cross-referral event
    track("lp.pre_screener.cross_referral", {
      from_landing_page_id: config.sourceLandingPageId,
      to_landing_page_id: config.targetLandingPageId,
      question_id: config.triggerQuestionId,
      answer_value: config.triggerAnswerValue,
    });

    // Build URL and redirect
    const url = buildCrossReferralUrl(config);
    window.location.href = url;
  }, [track]);

  // Check if an option should trigger a cross-referral redirect
  const shouldRedirect = useCallback((selectedOption: any, isDisqualifying: boolean): boolean => {
    return shouldRedirectToCrossReferral(selectedOption, isDisqualifying);
  }, []);

  // Handle prefilled data on component mount
  useEffect(() => {
    if (!crossReferralData.hasData) {
      return;
    }

    const { fromLandingPageId, answers: prefillAnswers } = crossReferralData;

    // Track cross-referral arrival
    track("lp.pre_screener.cross_referral_arrived", {
      from_landing_page_id: fromLandingPageId,
      to_landing_page_id: landingPageId,
      prefilled_answers: Object.keys(prefillAnswers),
    });

    // Merge prefilled answers with current answers
    const newAnswers = { ...currentAnswers, ...prefillAnswers };
    updateAnswers(newAnswers);

    // Check if we should auto-advance past the first question
    const firstQuestion = questions[0];
    if (firstQuestion && shouldAutoAdvanceQuestion(firstQuestion.id, prefillAnswers, 0)) {
      const nextIndex = getAutoAdvanceQuestionIndex(0, questions.length);
      updateQuestionnareState({
        currentQuestionIndex: nextIndex,
      });
    }

    // Handle BMI calculation if height and weight are provided
    const heightValue = prefillAnswers.height;
    const weightValue = prefillAnswers.weight;
    if (typeof heightValue === "string" && typeof weightValue === "string") {
      validateBMIUtil({
        heightValue,
        weightValue,
        minBMI: (() => {
          const raw = questions.find((q) => q.id === "weight")?.minBMI;
          return typeof raw === "function" ? raw(prefillAnswers) : raw;
        })(),
        maxBMI: questions.find((q) => q.id === "weight")?.maxBMI,
        onBmiCalculated: (calculatedBmi) => {
          updateQuestionnareState({
            bmi: calculatedBmi > 0 ? calculatedBmi : null,
          });
        },
      });
    }
  }, []); // Only run once on mount

  return {
    isFromCrossReferral: crossReferralData.hasData,
    fromLandingPageId: crossReferralData.fromLandingPageId,
    prefillAnswers: crossReferralData.answers,
    handleCrossReferralRedirect,
    shouldRedirect,
  };
} 